import { Video } from 'lucide-react-native';
import { type GestureResponderEvent, Linking } from 'react-native';
import { HStack, Text } from '../../../components/primitives';
// Not re-exported from the primitives barrel — same import path the rest of
// the calendar feature uses (EventFormModal, CalendarPeriodHeader). Button
// already enforces the 44/48px minimum target, so no size override here.
import { Button } from '../../../components/primitives/Button';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';

interface JoinCallButtonProps {
  /** CalendarEvent.conferenceUrl — nullable, and untrusted when provider-synced. */
  conferenceUrl: string | null | undefined;
  eventId: string;
}

/**
 * `conferenceUrl` has been on the event row and the type since the model was
 * written, and nothing rendered it: reaching a meeting link meant opening the
 * event. This is the one-tap path from the block itself.
 *
 * The url can arrive from an external provider sync, so it is untrusted input.
 * Only https is opened — `javascript:` and `file:` are the reason the check
 * exists, and plain `http:` is refused rather than dropping a rep onto a
 * cleartext meeting link. An unusable url renders nothing at all, so a rep is
 * never offered an action that will fail.
 */
function isJoinableUrl(url: string | null | undefined): url is string {
  if (!url) return false;

  try {
    return new URL(url.trim()).protocol === 'https:';
  } catch {
    // Not absolute, so not something we can hand to the OS.
    return false;
  }
}

export function JoinCallButton({ conferenceUrl, eventId }: Readonly<JoinCallButtonProps>) {
  const { t } = useTranslation();

  if (!isJoinableUrl(conferenceUrl)) {
    return null;
  }

  const url = conferenceUrl.trim();

  return (
    <Button
      testID={`calendar_join_call_${eventId}`}
      variant="primary"
      size="sm"
      accessibilityRole="button"
      accessibilityLabel={t('calendar.joinCall.accessibilityLabel')}
      // The surrounding event block is itself pressable and opens the edit
      // form. Without this, joining a call would also open the form behind it.
      onPress={(event?: GestureResponderEvent & { stopPropagation?: () => void }) => {
        event?.stopPropagation?.();
        void Linking.openURL(url);
      }}
    >
      <HStack spacing="xs" align="center">
        <Video size={14} color={colors.text.inverse} strokeWidth={2} />
        <Text variant="caption" color={colors.text.inverse} style={{ fontWeight: '700' }}>
          {t('calendar.joinCall.label')}
        </Text>
      </HStack>
    </Button>
  );
}
